import React, { useCallback, useEffect, useRef, useState } from 'react';

import { LayerTogglePanel } from '../../maps/LayerTogglePanel';
import { useMapDraw, DrawMode } from '../../../hooks/useMapDraw';
import { useMapEngine } from '../../../hooks/useMapEngine';
import { clearDraft, loadDraft, saveDraft } from '../../../utils/draftStorage';
import { downloadBlob } from '../../../utils/exportPng';
import { computeFeaturesBbox } from '../../../utils/featureBbox';
import {
  featureCollectionToLayers,
  hasMultipleLayers,
} from '../../../utils/featureCollectionToLayers';
import type { GeoJSONFeatureCollection, MapView } from '../../../types/gis';

interface MapArtifactViewerProps {
  artifact: any;
  onSave?: (fc: GeoJSONFeatureCollection, view: MapView) => Promise<void>;
  onThumbnailUpdate?: (artifactId: string, dataUri: string) => Promise<void>;
  // Cached Stack Health signal for `basemap_tiles`. When 'amber'/'red' AND
  // user has selected a gated style (satellite/terrain/outdoor), a toast
  // surfaces with one-click "Switch to Streets" remediation per spec § 4.15.1.
  basemapHealthStatus?: 'green' | 'amber' | 'red';
}

const RETRY_DELAYS_MS = [2000, 5000, 15000];
const GATED_STYLES = new Set(['satellite', 'terrain', 'outdoor']);

export const MapArtifactViewer: React.FC<MapArtifactViewerProps> = ({
  artifact,
  onSave,
  onThumbnailUpdate,
  basemapHealthStatus,
}) => {
  const [mode, setMode] = useState<DrawMode>('view');
  const [styleId, setStyleId] = useState<string>(
    (artifact?.metadata?.map_view?.style_id as string) || 'streets',
  );
  const [currentFc, setCurrentFc] = useState<GeoJSONFeatureCollection>(
    // GET /api/artifacts/{id} returns the FC under `content` (matches every
    // other viewer in the codebase). Older drafts of this viewer used `data`
    // (matching my PATCH return — also fixed) — keep both as fallbacks for
    // any in-flight rehydrated state. Defaults to empty FC if neither.
    ((artifact?.content ?? artifact?.data) as GeoJSONFeatureCollection)
    || { type: 'FeatureCollection', features: [] },
  );
  const [savingState, setSavingState] = useState<'idle' | 'saving' | 'failed'>('idle');
  const [hiddenLayers, setHiddenLayers] = useState<ReadonlySet<string>>(() => new Set());
  const retryAttemptRef = useRef(0);
  const isDirtyRef = useRef(false);

  // Resume-draft prompt — only fires for an artifact that has a saved draft.
  useEffect(() => {
    if (!artifact?.id) return;
    const draft = loadDraft(artifact.id);
    if (draft && typeof window !== 'undefined' && window.confirm
        && window.confirm('Resume your unsaved edits?')) {
      setCurrentFc(draft.data);
    } else if (draft) {
      clearDraft(artifact.id);
    }
  }, [artifact?.id]);

  // beforeunload guard — block close while save in flight or unsaved edits.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (savingState === 'saving' || isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [savingState]);

  const engine = useMapEngine({
    geojson: currentFc,
    view: artifact?.metadata?.map_view,
    styleId: styleId as any,
    interactive: true,
    preserveDrawingBuffer: true,
    hiddenLayers,
  });

  const handleToggleLayer = useCallback((layerName: string) => {
    setHiddenLayers((prev) => {
      const next = new Set(prev);
      if (next.has(layerName)) next.delete(layerName);
      else next.add(layerName);
      return next;
    });
  }, []);

  const handleFitToLayer = useCallback((layerName: string) => {
    if (!engine.mapInstance) return;
    const buckets = featureCollectionToLayers(currentFc);
    const features = buckets[layerName] || [];
    const bbox = computeFeaturesBbox(features);
    if (!bbox) return;
    engine.mapInstance.fitBounds(bbox, { padding: 30, duration: 600, maxZoom: 14 });
  }, [currentFc, engine.mapInstance]);

  const draw = useMapDraw({
    mapInstance: engine.mapInstance,
    geojson: currentFc,
    mode,
    onChange: (next) => {
      setCurrentFc(next);
      isDirtyRef.current = true;
      if (artifact?.id) {
        saveDraft(artifact.id, {
          data: next,
          metadata: { map_view: artifact.metadata?.map_view },
        });
      }
    },
  });

  // Mirror draw.isDirty into a ref so the beforeunload handler doesn't
  // need to re-bind on every dirty toggle.
  useEffect(() => {
    isDirtyRef.current = draw.isDirty;
  }, [draw.isDirty]);

  const handleSave = async (attempt = 0) => {
    if (!onSave) return;
    setSavingState('saving');
    try {
      const view: MapView = (artifact?.metadata?.map_view as MapView) || {
        center: [0, 0], zoom: 1, bearing: 0, style_id: styleId,
      };
      // Step 1: persist data + map_view.
      await onSave(currentFc, view);

      // Step 2: capture thumbnail AFTER the save succeeds (per spec § 4.14.2).
      // Failure here does NOT trigger save retry — the artifact is already
      // saved; we just log and skip the thumbnail update. Thumbnails do not
      // go through saveDraft (would balloon localStorage with ~50KB data URIs).
      try {
        const thumbnailDataUri = await engine.exportThumbnail({ w: 256, h: 160 });
        if (artifact?.id && onThumbnailUpdate) {
          await onThumbnailUpdate(artifact.id, thumbnailDataUri).catch((e) =>
            console.warn('thumbnail update failed (artifact saved without thumbnail)', e),
          );
        }
      } catch (e) {
        console.warn('thumbnail export failed', e);
      }

      if (artifact?.id) clearDraft(artifact.id);
      isDirtyRef.current = false;
      setSavingState('idle');
      retryAttemptRef.current = 0;
    } catch {
      if (attempt < RETRY_DELAYS_MS.length) {
        setTimeout(() => handleSave(attempt + 1), RETRY_DELAYS_MS[attempt]);
      } else {
        setSavingState('failed');
      }
    }
  };

  const handleExportPng = async () => {
    try {
      const blob = await engine.exportPng();
      const filename = artifact?.name
        ? `${artifact.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${new Date().toISOString().slice(0, 10)}.png`
        : `map-${new Date().toISOString().slice(0, 10)}.png`;
      downloadBlob(blob, filename);
    } catch (e) {
      console.warn('PNG export failed', e);
    }
  };

  const handleSaveCopy = () => {
    const fcBlob = new Blob([JSON.stringify(currentFc, null, 2)], { type: 'application/geo+json' });
    downloadBlob(fcBlob, `map-${new Date().toISOString().slice(0, 10)}.geojson`);
  };

  const fc = currentFc;
  const showLayerPanel = hasMultipleLayers(fc);

  const showBasemapToast = !!basemapHealthStatus
    && (basemapHealthStatus === 'amber' || basemapHealthStatus === 'red')
    && GATED_STYLES.has(styleId);

  return (
    <div className="map-artifact-viewer flex flex-col h-full">
      <div className="toolbar flex items-center gap-2 p-2 border-b border-gray-300 bg-gray-50">
        <select
          value={styleId}
          onChange={(e) => setStyleId(e.target.value)}
          className="text-xs"
          aria-label="Map style"
        >
          <option value="streets">Streets</option>
          <option value="satellite">Satellite</option>
          <option value="terrain">Terrain</option>
          <option value="outdoor">Outdoor</option>
        </select>
        <div className="flex border rounded overflow-hidden text-xs">
          {(['view', 'edit', 'draw'] as DrawMode[]).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-2 py-1 ${mode === m ? 'bg-emerald-600 text-white' : 'bg-white'}`}
            >
              {m.charAt(0).toUpperCase() + m.slice(1)}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          <button
            onClick={() => handleSave()}
            className="text-xs px-2 py-1 rounded bg-emerald-600 text-white"
          >
            Save
          </button>
          <button onClick={handleExportPng} className="text-xs px-2 py-1 rounded border">
            ↓ PNG
          </button>
        </div>
      </div>

      <div ref={engine.containerRef} style={{ flex: 1, minHeight: 360 }} />

      {showLayerPanel && (
        <LayerTogglePanel
          fc={fc}
          hiddenLayers={hiddenLayers}
          onToggle={handleToggleLayer}
          onFitToLayer={handleFitToLayer}
        />
      )}

      {savingState === 'saving' && (
        <div className="p-2 text-xs bg-blue-50">Saving…</div>
      )}
      {savingState === 'failed' && (
        <div className="p-2 text-xs bg-red-50 flex items-center gap-2">
          <span>Save failed — check your connection.</span>
          <button onClick={() => handleSave()} className="px-2 py-0.5 rounded border">Retry</button>
          <button onClick={handleSaveCopy} className="px-2 py-0.5 rounded border">Save copy</button>
        </div>
      )}

      {/* Mid-session basemap degradation toast (spec § 4.15.1) */}
      {showBasemapToast && (
        <div className="p-2 text-xs bg-yellow-50 flex items-center gap-2 border-t border-yellow-300">
          <span>
            {styleId.charAt(0).toUpperCase() + styleId.slice(1)} tiles degraded —
            Stack Health flagged the tile provider.
          </span>
          <button
            onClick={() => setStyleId('streets')}
            className="px-2 py-0.5 rounded border bg-white"
          >
            Switch to Streets
          </button>
        </div>
      )}
    </div>
  );
};
